import React, { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import Footer from "@/components/Footer";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import BackgroundMedia from "@/components/BackgroundMedia";
import BookingForm from "@/components/BookingForm";
import Seo from "@/components/Seo";
import { Container, SectionLabel } from "@/components/primitives";
import { useSettings } from "@/context/SettingsContext";
import { api, mediaSrc } from "@/lib/api";

const DEFAULT_SIDE_IMG = "https://images.unsplash.com/photo-1771797573645-7483eab8207f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Contact() {
  const { settings } = useSettings();
  const { company, socials } = settings;
  const [doc, setDoc] = useState(null);
  useEffect(() => { api.getPage("contact").then(setDoc).catch(() => {}); }, []);

  const c = doc?.content || {};
  const hero = c.hero || {};
  const booking = c.booking || {};
  const heroEyebrow = hero.eyebrow || "BEGIN";
  const heroHeadline = hero.headline || "Let's begin\na conversation.";
  const heroIntro = hero.intro || "For new projects, collaborations or visits to the Experience Center. We respond within one working day.";
  const heroImage = hero.image_url || "";
  const heroVideo = hero.video_url || "";
  const hasBackdrop = !!(heroImage || heroVideo);
  const sideRaw = c.media?.side_image_url ?? (typeof c.side_image_url === "string" ? c.side_image_url : "");
  const sideImg = sideRaw ? mediaSrc(sideRaw) : DEFAULT_SIDE_IMG;
  const bookingTitle = booking.title || "Request a Consultation";
  const altLabel = c.alt_label || "OR REACH US DIRECTLY";

  const links = [
    company.phone && { label: `Phone · ${company.phone}`, href: `tel:${company.phone.replace(/[^\d+]/g, "")}`, testId: "contact-phone" },
    company.email && { label: `Email · ${company.email}`, href: `mailto:${company.email}`, testId: "contact-email" },
    { label: `WhatsApp${socials.whatsapp_display ? ` · ${socials.whatsapp_display}` : ""}`, href: `https://wa.me/${socials.whatsapp_number}`, testId: "contact-whatsapp" },
    { label: `Instagram · @${socials.instagram_handle}`, href: socials.instagram_url, testId: "contact-instagram" },
    { label: `LinkedIn · ${company.name}`, href: socials.linkedin_url, testId: "contact-linkedin" },
  ].filter(Boolean);

  const ml = (t) => t.split("\n").map((line, i) => <span key={i} className="block">{line}</span>);

  const altBlock = (
    <div className="border-t border-lux-line pt-6 flex flex-col gap-3.5">
      <p className="text-[11px] tracking-[0.28em] uppercase text-lux-accent">{altLabel}</p>
      {links.map((l) => (
        <a key={l.testId} href={l.href} target={l.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={l.testId} className="font-serif text-xl text-lux-ink font-light hover:text-lux-accent transition-colors">
          {l.label}
        </a>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-lux-bg">
      <Seo title="Contact · Onedot6" description={heroIntro} path="/contact" />
      <TopBar />
      {hasBackdrop && (
        <div className="relative min-h-[420px] md:min-h-[520px] flex items-end overflow-hidden">
          <BackgroundMedia videoUrl={heroVideo} imageUrl={heroImage} testId="contact-hero-media" />
          <div className="absolute inset-0 bg-gradient-to-b from-lux-ink/55 via-lux-ink/25 to-lux-ink/75 pointer-events-none" />
          <Container className="relative pt-40 pb-12 md:pb-16">
            <div className="max-w-[720px]">
              <SectionLabel light>{heroEyebrow}</SectionLabel>
              <h1 className="font-serif text-white font-light tracking-tight leading-none mt-2 text-5xl md:text-7xl">{ml(heroHeadline)}</h1>
              <p className="text-white/85 font-light leading-relaxed mt-5 text-base md:text-lg max-w-[520px]">{heroIntro}</p>
            </div>
          </Container>
        </div>
      )}
      <Container className={`${hasBackdrop ? "pt-14 md:pt-20" : "pt-32 md:pt-40"} pb-24`}>
        <div className="flex flex-col md:flex-row gap-12 md:gap-20">
          <div className="flex-1">
            {!hasBackdrop && (
              <div className="mb-10">
                <SectionLabel>{heroEyebrow}</SectionLabel>
                <h1 className="font-serif text-lux-ink font-light tracking-tight leading-none mt-2 text-5xl md:text-7xl">{ml(heroHeadline)}</h1>
                <p className="text-lux-sec font-light leading-relaxed mt-5 text-base md:text-lg max-w-[520px]">{heroIntro}</p>
              </div>
            )}
            {altBlock}
          </div>
          <div className="flex-1">
            <img src={sideImg} alt="" className="w-full h-56 md:h-72 object-cover bg-lux-line mb-8" data-testid="contact-side-image" />
            <BookingForm type="consultation" title={bookingTitle} />
          </div>
        </div>
      </Container>
      <Footer />
      <WhatsAppFAB />
    </div>
  );
}
